// client/src/components/marketer/modals/MarketerApplicationsViewModal.js
import React, { useState, useEffect } from 'react';
import '../../../styles/marketer/modals/MarketerApplicationsModal.css';

const API_URL = process.env.REACT_APP_API_URL;

export default function MarketerApplicationsViewModal({ open, onClose, projectId, projectNumber }) {
  const [loading, setLoading] = useState(false);
  const [applications, setApplications] = useState([]);
  const [projectInfo, setProjectInfo] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/marketer/projects/${projectId}/applications`, {
        credentials: 'include'
      });
      const data = await res.json();
      
      if (data.ok) {
        setApplications(data.applications || []);
        setProjectInfo(data.project || null);
      } else {
        alert('지원자 목록 조회에 실패했습니다.');
      }
    } catch (error) {
      console.error('지원자 목록 조회 오류:', error);
      alert('지원자 목록 조회 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && projectId) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectId]);

  if (!open) return null;

  // 통계 계산
  const statistics = {
    total: applications.length,
    accepted: applications.filter(a => a.contractStatus === 'accepted').length,
    pending: applications.filter(a => a.contractStatus === 'pending').length,
    selected: applications.filter(a => a.status === 'selected' && a.contractStatus === 'none').length,
    applicationOnly: applications.filter(a => a.status === 'pending').length
  };

  // SNS 아이콘
  const getSNSIcon = (platform) => {
    const icons = {
      instagram: '📷',
      youtube: '📺',
      blog: '📝',
      tiktok: '🎵'
    };
    return icons[platform] || '📱';
  };

  // 팔로워 수 포맷
  const formatFollowers = (count) => {
    if (count >= 10000) {
      return `${Math.floor(count / 10000)}만`;
    }
    return count?.toLocaleString() || '-';
  };

  // 상태 텍스트
  const getStatusText = (app) => {
    if (app.contractStatus === 'accepted') {
      return '✅ 계약 완료';
    }
    if (app.contractStatus === 'rejected') {
      return '❌ 계약 거절';
    }
    if (app.contractStatus === 'pending') {
      return '⏳ 수락 대기 중';
    }
    if (app.contractStatus === 'expired') {
      return '⏰ 계약 미진행';
    }
    if (app.status === 'selected') {
      return '📨 선정됨 (계약요청 전송)';
    }
    if (app.status === 'pending') {
      return '⏸️ 미선정';
    }
    if (app.status === 'expired') {
      return '⏰ 선정 만료';
    }
    return '-';
  };

  // 상태 클래스
  const getStatusClass = (app) => {
    if (app.contractStatus === 'accepted') return 'marketer-applications-modal__status--accepted';
    if (app.contractStatus === 'pending') return 'marketer-applications-modal__status--pending';
    if (app.status === 'selected') return 'marketer-applications-modal__status--selected';
    if (app.contractStatus === 'rejected') return 'marketer-applications-modal__status--rejected';
    if (app.contractStatus === 'expired' || app.status === 'expired') return 'marketer-applications-modal__status--expired';
    return '';
  };

  return (
    <div className="marketer-applications-modal mbm-overlay">
      <div className="marketer-applications-modal__content mbm">
        {/* 헤더 */}
        <div className="marketer-applications-modal__header mbm-head">
          <h2 className="marketer-applications-modal__title mbm-title">
            인플루언서 선정 계약 상세
          </h2>
          <button onClick={onClose} className="marketer-applications-modal__close mbm-close">
            ×
          </button>
        </div>

        {/* 바디 */}
        <div className="marketer-applications-modal__body">
          {loading ? (
            <div className="marketer-applications-modal__loading">
              로딩 중...
            </div>
          ) : (
            <>
              {/* 프로젝트 정보 */}
              <div className="marketer-applications-modal__project-info">
                <div className="marketer-applications-modal__project-number">
                  Project NO.{projectNumber || 'N/A'}
                </div>
                <div className="marketer-applications-modal__stats">
                  <span className="marketer-applications-modal__stat--accepted">
                    ✅ {statistics.accepted}명 계약완료
                  </span>
                  <span className="marketer-applications-modal__stat--pending">
                    ⏳ {statistics.pending}명 수락대기
                  </span>
                  <span className="marketer-applications-modal__stat--selected">
                    📨 {statistics.selected}명 선정됨
                  </span>
                  <span className="marketer-applications-modal__stat--total">
                    👥 {projectInfo?.recruit?.headcount || 0}명 모집
                  </span>
                  <span className="marketer-applications-modal__stat--count">
                    (총 {statistics.total}명 지원)
                  </span>
                </div>
              </div>

              {/* 지원자 테이블 */}
              {applications.length === 0 ? (
                <div className="marketer-applications-modal__empty">
                  지원자가 없습니다.
                </div>
              ) : (
                <div className="marketer-applications-modal__table-container">
                  <table className="marketer-applications-modal__table">
                    <thead>
                      <tr>
                        <th>NO</th>
                        <th>인플루언서</th>
                        <th>만족도 점수</th>
                        <th>지원한 SNS 채널</th>
                        <th>취소 횟수</th>
                        <th>상태</th>
                      </tr>
                    </thead>
                    <tbody>
                      {applications.map((app, idx) => (
                        <tr key={app.id}>
                          <td>{idx + 1}</td>
                          <td>
                            <div className="marketer-applications-modal__influencer">
                              <div className="marketer-applications-modal__influencer-name">
                                {app.influencer?.name || '-'}
                              </div>
                              <div className="marketer-applications-modal__influencer-id">
                                {app.influencer?.userId || ''}
                              </div>
                            </div>
                          </td>
                          <td>
                            {app.influencer?.satisfactionScore > 0 ? (
                              <span className="marketer-applications-modal__score marketer-applications-modal__score--average">
                                ⭐ {app.influencer.satisfactionScore.toFixed(1)}점 / 5점만점
                              </span>
                            ) : (
                              <span className="marketer-applications-modal__score marketer-applications-modal__score--none">아직 평가 없음</span>
                            )}
                          </td>
                          <td>
                            {app.snsAccounts && app.snsAccounts.length > 0 ? (
                              <div className="marketer-applications-modal__sns">
                                {app.snsAccounts.map((sns, snsIdx) => (
                                  <a
                                    key={snsIdx}
                                    href={sns.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="marketer-applications-modal__sns-item"
                                  >
                                    <span>{getSNSIcon(sns.platform)}</span>
                                    <span style={{ fontWeight: 600 }}>
                                      {sns.accountName || '-'}
                                    </span>
                                    <span style={{ color: '#6b7686' }}>
                                      ({formatFollowers(sns.followerCount)})
                                    </span>
                                  </a>
                                ))}
                              </div>
                            ) : (
                              <span style={{ color: '#9ca3af' }}>-</span>
                            )}
                          </td>
                          <td>
                            <span className={
                              (app.influencer?.contractCancelCount || 0) === 0 
                                ? 'marketer-applications-modal__cancel-count--zero' 
                                : (app.influencer?.contractCancelCount || 0) >= 3 
                                  ? 'marketer-applications-modal__cancel-count--danger' 
                                  : 'marketer-applications-modal__cancel-count--warning'
                            }>
                              {app.influencer?.contractCancelCount || 0}회
                            </span>
                          </td>
                          <td>
                            <span className={`marketer-applications-modal__status ${getStatusClass(app)}`}>
                              {getStatusText(app)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        {/* 푸터 */}
        <div className="marketer-applications-modal__footer mbm-foot">
          <button onClick={onClose} className="mb-btn mb-btn-secondary">
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}
